import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SidePanel from 'components/sidePanel/SidePanel';
import { SidePanelDirection } from 'components/sidePanel/SidePanel.types';
import InfoSvg from 'assets/images/icons/navbar/info.svg?react';
import GuestExperienceSvg from 'assets/images/icons/navbar/guest-experience.svg?react';
import HostfullyDevicesSvg from 'assets/images/icons/navbar/hostfully-devices.svg?react';
import AdminAccessSvg from 'assets/images/icons/navbar/admin-access.svg?react';
import useSidePanel from 'components/sidePanel/useSidePanel';
import { normalizeKey } from 'utils/localeUtils';
import useAppUser from 'hooks/useAppUser';
import useGetGuidebookAccessUrl from 'hooks/guidebook/useGetGuidebookAccessUrl';
import useNotify from 'hooks/useNotify';
import useGetHostfullyDevicesUrl from 'hooks/hostfullyDevices/useGetHostfullyDevicesUrl';
import useLoginasModal from 'pages/loginas/useLoginasModal';
import { redirectInNewTab } from 'utils/urlUtils';
import type { NavigationProps } from '../Header.types';
import Footer from '../../footer/Footer';
import ProfileAvatar from '../profileAvatar/ProfileAvatar';
import NavigationLogo from '../navigation/NavigationLogo';
import AddLeadButton from '../navigation/AddLeadButton';
import useNavigationDropdownTitle from '../useNavigationDropdownTitle';
import useNavigationMethods from '../useNavigationMethods';
import useHostfullyDevices from '../useHostfullyDevices';
import useGuidebooks from '../useGuidebooks';
import {
  Container,
  HamburgerMenuButton,
  MenuItem,
  DropdownTrigger,
  DropdownMenu,
  AccountContainer,
  CloseButton,
} from './MobileNavigation.styles';

const sidePanelId = 'mobile-navigation-panel';

const MobileNavigation = ({
  navItems,
  dropdownItems,
  dropdownSecondaryItems,
}: NavigationProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isSuperAdmin } = useAppUser();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { toggleSidePanel, closeSidePanel, triggerClassName } = useSidePanel({
    sidePanelId,
  });
  const { isDropdownActive, isNavItemActive } = useNavigationMethods();
  const navDropdownTitle = useNavigationDropdownTitle();
  const { getGuidebookAccessUrl } = useGetGuidebookAccessUrl();
  const { getHostfullyDevicesUrl } = useGetHostfullyDevicesUrl();
  const { openLoginasModal } = useLoginasModal();
  const { notifyError } = useNotify();
  const { isHostfullyDevicesEnabled } = useHostfullyDevices();
  const { isGuidebooksEnabled } = useGuidebooks();

  const handleCloseMenu = () => {
    closeSidePanel();
    setIsDropdownOpen(false);
  };

  const redirectTo = (path: string) => {
    navigate(path.slice(1));
    handleCloseMenu();
  };

  const redirectToGuidebook = async () => {
    redirectInNewTab({
      getUrl: async () => {
        const guidebookUrl = await getGuidebookAccessUrl();
        return guidebookUrl?.data?.url;
      },
      onError: () => notifyError(t('common.guidebookAccessError')),
    });
  };

  const redirectToHostfullyDevices = async () => {
    redirectInNewTab({
      getUrl: async () => {
        const hostfullyDevicesUrl = await getHostfullyDevicesUrl();
        return hostfullyDevicesUrl?.data?.url;
      },
      onError: () => notifyError(t('common.hostfullyDevicesAccessError')),
    });
  };

  return (
    <Container data-testid="mobile-navigation" id="webapp-header">
      <NavigationLogo />
      <HamburgerMenuButton
        data-testid="mobile-navigation-menu-icon"
        className={triggerClassName}
        onClick={toggleSidePanel}
      />

      <SidePanel
        data-testid="mobile-navigation-menu-container"
        direction={SidePanelDirection.RIGHT_TO_LEFT}
        sidePanelId={sidePanelId}
      >
        <AccountContainer>
          <div>
            <ProfileAvatar onClick={handleCloseMenu} />
            <AddLeadButton onMobileClick={handleCloseMenu} />
            <CloseButton
              onClick={handleCloseMenu}
              data-testid="mobile-navigation-close-icon"
            />
          </div>
        </AccountContainer>
        {navItems.map((item) => (
          <MenuItem
            active={isNavItemActive(item.path)}
            key={item.path}
            onClick={() => redirectTo(item.path)}
          >
            {t(normalizeKey(item.labelKey))}
          </MenuItem>
        ))}
        <DropdownTrigger
          isOpen={isDropdownOpen}
          onClick={() => setIsDropdownOpen((s) => !s)}
          active={isDropdownActive({ dropdownItems, dropdownSecondaryItems })}
        >
          <div>{navDropdownTitle}</div>
        </DropdownTrigger>
        <DropdownMenu
          itemsAmount={
            (dropdownItems?.length || 0) + (dropdownSecondaryItems?.length || 0)
          }
          isOpen={isDropdownOpen}
        >
          {dropdownItems?.map((item) => (
            <MenuItem
              active={isNavItemActive(item.path)}
              key={item.path}
              onClick={() => redirectTo(item.path)}
            >
              {t(normalizeKey(item.labelKey))}
            </MenuItem>
          ))}
          {dropdownSecondaryItems?.map((item) => (
            <MenuItem
              active={isNavItemActive(item.path)}
              key={item.path}
              onClick={() => redirectTo(item.path)}
            >
              {t(normalizeKey(item.labelKey))}
            </MenuItem>
          ))}
        </DropdownMenu>
        <MenuItem
          onClick={() => window.open('https://help.hostfully.com', '_blank')}
          data-testid="navbar-help-icon"
        >
          <InfoSvg /> {t('common.helpAndDocumentation')}
        </MenuItem>
        {isGuidebooksEnabled && (
          <MenuItem
            onClick={redirectToGuidebook}
            data-testid="navbar-guest-experience-icon"
          >
            <GuestExperienceSvg /> <span>{t('common.guestExperience')}</span>
          </MenuItem>
        )}
        {isHostfullyDevicesEnabled && (
          <MenuItem
            onClick={redirectToHostfullyDevices}
            data-testid="navbar-hostfully-devices-icon"
          >
            <HostfullyDevicesSvg /> <span>{t('common.hostfullyDevices')}</span>
          </MenuItem>
        )}
        {isSuperAdmin && (
          <MenuItem
            onClick={openLoginasModal}
            data-testid="navbar-admin-access-icon"
          >
            <AdminAccessSvg /> <span>{t('common.adminAccess')}</span>
          </MenuItem>
        )}

        <Footer data-testid="mobile-navigation-footer" />
      </SidePanel>
    </Container>
  );
};

export default MobileNavigation;
